import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../i18n';

import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Zap, Loader2 } from 'lucide-react';

export default function RegisterPage() {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [localError, setLocalError] = useState('');
  const { register, error } = useAuth();
  const navigate = useNavigate();
  const { t } = useI18n();
  const redirect = searchParams.get('redirect') || '/';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError('');
    if (password.length < 6) {
      setLocalError(t('lang') === 'sv' ? 'Lösenordet måste vara minst 6 tecken' : 'Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    const result = await register(email, password, name);
    setLoading(false);
    if (result.success) navigate(redirect);
    else setLocalError(result.error);
  };

  return (
    <div className="min-h-screen bg-[#09090B] flex items-center justify-center p-4">

      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-yellow-400 to-orange-500 mb-4">
            <Zap className="w-6 h-6 text-black" strokeWidth={2} />
          </div>
          <h1 className="text-2xl font-semibold text-zinc-50 font-['Outfit'] tracking-tight">{t('register')}</h1>
          <p className="text-zinc-500 mt-2 text-sm">{t('registerSubtitle')}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name" className="text-zinc-400 text-sm">{t('name')}</Label>
            <Input id="name" type="text" value={name} onChange={(e) => setName(e.target.value)}
              className="bg-[#141416] border-white/10 text-zinc-50 placeholder:text-zinc-600 h-11 focus:border-zinc-500"
              data-testid="register-name-input" required />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email" className="text-zinc-400 text-sm">{t('email')}</Label>
            <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="din@email.com"
              className="bg-[#141416] border-white/10 text-zinc-50 placeholder:text-zinc-600 h-11 focus:border-zinc-500"
              data-testid="register-email-input" required />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password" className="text-zinc-400 text-sm">{t('password')}</Label>
            <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
              className="bg-[#141416] border-white/10 text-zinc-50 placeholder:text-zinc-600 h-11 focus:border-zinc-500"
              data-testid="register-password-input" required />
          </div>
          {(localError || error) && (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm" data-testid="register-error">{localError || error}</div>
          )}
          <Button type="submit" disabled={loading} className="w-full bg-zinc-100 text-zinc-900 hover:bg-white h-11 font-medium" data-testid="register-submit-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t('register')}
          </Button>
        </form>

        <p className="text-center mt-6 text-zinc-500 text-sm">
          {t('hasAccount')}{' '}
          <Link to="/login" className="text-zinc-100 hover:text-white transition-colors" data-testid="login-link">{t('login')}</Link>
        </p>
      </div>
    </div>
  );
}
